/**
 * BulletList — typed renderer for the `bullets` payload.
 *
 * Each row is `heading` (optional) + `point`, with an optional `detail` line
 * and its citation chips. Consecutive rows sharing a heading render under one
 * heading so the list reads as grouped rather than repetitive; `groups` (the
 * pre-grouped shape) is flattened into the same rows.
 */

import React from 'react';
import { BulletItem, BulletsPayload, ResearchCitation } from '../types';
import { CitationChips, CitationHandlers, citationNumbers } from './CitationChip';
import InlineCitedText from './InlineCitedText';

interface Props extends CitationHandlers {
  payload: BulletsPayload;
  citations: ResearchCitation[];
}

/** The bullet text, under either spelling the engine has used. */
const bulletText = (bullet: BulletItem): string => bullet.point || bullet.text || '';

/** Flatten `bullets` + `groups` into one ordered list, keeping headings. */
function flatten(payload: BulletsPayload): BulletItem[] {
  const rows: BulletItem[] = Array.isArray(payload.bullets) ? [...payload.bullets] : [];
  if (Array.isArray(payload.groups)) {
    payload.groups.forEach((group) => {
      (group.bullets || []).forEach((bullet) =>
        rows.push({ ...bullet, heading: bullet.heading || group.heading })
      );
    });
  }
  return rows;
}

const BulletList: React.FC<Props> = ({ payload, citations, ...handlers }) => {
  const rows = flatten(payload);

  return (
    <div className="font-sans space-y-3 animate-fade-in" data-testid="bullet-list">
      {payload.title && (
        <h3 className="text-sm font-semibold text-fg-strong">{payload.title}</h3>
      )}
      {payload.summary && (
        <p className="text-sm leading-relaxed text-fg-body">
          <InlineCitedText text={payload.summary} citations={citations} {...handlers} />
        </p>
      )}

      <ul className="space-y-2.5">
        {rows.map((bullet, i) => {
          const text = bulletText(bullet);
          // Only show the heading when it changes — repeated labels are noise.
          const showHeading = !!bullet.heading && bullet.heading !== rows[i - 1]?.heading;
          if (!text && !showHeading) return null;
          return (
            <li
              key={i}
              data-testid="bullet-row"
              className="reveal-item"
              style={{ '--reveal-delay': `${Math.min(i, 10) * 50}ms` } as React.CSSProperties}
            >
              {showHeading && (
                <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted mb-1">
                  {bullet.heading}
                </p>
              )}
              {text && (
                <div className="flex items-start gap-2">
                  <span
                    className="mt-[7px] w-1.5 h-1.5 rounded-full bg-champagne flex-shrink-0"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="text-sm leading-relaxed text-fg-body">
                      <InlineCitedText text={text} citations={citations} {...handlers} />
                      <CitationChips
                        numbers={citationNumbers(bullet)}
                        citations={citations}
                        {...handlers}
                      />
                    </p>
                    {bullet.detail && (
                      <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
                        <InlineCitedText
                          text={bullet.detail}
                          citations={citations}
                          {...handlers}
                        />
                      </p>
                    )}
                    {bullet.sub_bullets && bullet.sub_bullets.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {bullet.sub_bullets.map((sub, j) => (
                          <li key={j} className="text-xs text-fg-muted leading-relaxed pl-3">
                            <InlineCitedText text={sub} citations={citations} {...handlers} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default BulletList;
